import { motion } from "framer-motion";
import {
  Award,
  CheckCircle,
  Globe,
  TrendingUp,
  FileCheck,
  Scale,
  Shield,
  Leaf,
  BadgeCheck,
  ScrollText,
  Sparkles,
  ArrowRight,
  Check,
  Star,
  Zap,
  Target,
  Lock,
} from "lucide-react";

export function Certifications() {
  const certifications = [
    {
      icon: Award,
      title: "ISCC Certification",
      description:
        "International Sustainability & Carbon Certification for all 2G & 3G bio-feedstocks",
      features: [
        "Sustainability verification",
        "Carbon savings documentation",
        "Origin traceability proof",
        "Global recognition",
      ],
    },
    {
      icon: TrendingUp,
      title: "Carbon Impact Validation",
      description: "Automated GHG savings mapping and reporting",
      features: [
        "Real-time carbon footprint tracking",
        "Emission reduction metrics",
        "Sustainability reporting",
        "Net-Zero alignment",
      ],
    },
    {
      icon: Globe,
      title: "Export Audit Compliance",
      description: "Meeting international standards for global markets",
      features: [
        "International audit readiness",
        "Export documentation",
        "Global traceability standards",
        "Cross-border compliance",
      ],
    },
    {
      icon: Scale,
      title: "Policy & Legal Compliance",
      description: "Full adherence to regulatory frameworks",
      features: [
        "Government mandate compliance",
        "Legal documentation",
        "Policy alignment",
        "Regulatory reporting",
      ],
    },
  ];

  const pathway = [
    {
      icon: FileCheck,
      title: "Digital Documentation",
      description: "Complete paperless FBO onboarding & verification",
    },
    {
      icon: Leaf,
      title: "Sustainability Metrics",
      description: "Automated carbon impact and GHG savings tracking",
    },
    {
      icon: BadgeCheck,
      title: "Quality Assurance",
      description: "Multi-point quality checks and certification tagging",
    },
    {
      icon: Globe,
      title: "Global Standards",
      description: "ISCC+ ready exports to international markets",
    },
  ];

  return (
    <section
      id="certifications"
      className="relative py-12 lg:py-16 bg-gradient-to-br from-white via-gray-50 to-white overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern
              id="certGrid"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#certGrid)" />
        </svg>
      </div>

      {/* Decorative Blobs */}
      <div className="absolute top-20 left-0 w-96 h-96 bg-[#1976D2]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-0 w-96 h-96 bg-[#1976D2]/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Creative Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-8"
        >
          {/* Top Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex justify-center mb-4"
          >
            <div className="inline-flex items-center gap-2 bg-white border-2 border-gray-200 rounded-full px-5 py-2.5 shadow-lg">
              <BadgeCheck className="w-5 h-5 text-[#1976D2]" />
              <span className="text-sm font-bold text-gray-900">
                World-Class Standards
              </span>
            </div>
          </motion.div>

          {/* Creative Title Layout */}
          <div className="relative max-w-5xl mx-auto text-center">
            {/* Floating Badges */}
            <motion.div
              animate={{ y: [0, -15, 0], rotate: [0, 10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-4 -left-4 lg:-left-8 bg-gradient-to-br from-[#1976D2] to-[#1565C0] rounded-xl p-3 shadow-2xl z-10"
            >
              <Award className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
            </motion.div>
            <motion.div
              animate={{ y: [0, 15, 0], rotate: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-4 -right-4 lg:-right-8 bg-gradient-to-br from-[#1976D2] to-[#1565C0] rounded-xl p-3 shadow-2xl z-10"
            >
              <Shield className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
            </motion.div>

            {/* Main Title */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 mb-3 leading-tight">
                Globally Recognized
                <span className="block mt-1 bg-gradient-to-r from-[#1976D2] to-[#1565C0] bg-clip-text text-transparent">
                  Compliance & Quality
                </span>
              </h2>

              <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-3xl mx-auto">
                Our feedstock meets the highest international sustainability standards, enabling{" "}
                <span className="font-bold text-gray-900">ISCC-certified exports</span>{" "}
                and comprehensive compliance documentation
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* Certification Cards - Mobile Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {certifications.map((cert, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-[#1976D2] to-[#1565C0] rounded-2xl opacity-20 group-hover:opacity-40 blur-xl transition-all" />
              <div className="relative h-full bg-white rounded-2xl overflow-hidden shadow-xl border-2 border-gray-100 group-hover:border-transparent transition-all">
                {/* Header with Gradient */}
                <div className="bg-gradient-to-br from-[#1976D2] to-[#1565C0] p-5 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12" />
                  
                  <div className="relative text-center">
                    <div className="bg-white/20 backdrop-blur-md rounded-xl p-3 shadow-xl group-hover:scale-110 transition-transform inline-flex mb-3">
                      <cert.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-black text-white mb-2">
                      {cert.title}
                    </h3>
                    <p className="text-white/90 text-xs leading-relaxed">
                      {cert.description}
                    </p>
                  </div>
                </div>

                {/* Features List */}
                <div className="p-4">
                  <div className="space-y-2">
                    {cert.features.map((feature, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 bg-gradient-to-r from-gray-50 to-transparent rounded-lg p-2 hover:from-[#1976D2]/5 transition-all group/item"
                      >
                        <div className="bg-gradient-to-br from-[#1976D2] to-[#1565C0] rounded-lg p-1 group-hover/item:scale-110 transition-transform flex-shrink-0">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-gray-700 font-semibold text-xs">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* KRB's Certification Pathway - Creative Redesign */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative mb-12"
        >
          {/* Section Title */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#1976D2]/10 to-[#1565C0]/10 border-2 border-[#1976D2]/30 rounded-full px-5 py-2.5 mb-4"
            >
              <ScrollText className="w-5 h-5 text-[#1976D2]" />
              <span className="text-sm font-bold text-gray-900">
                Our Process
              </span>
            </motion.div>

            <h3 className="text-2xl lg:text-3xl font-black text-gray-900 mb-3">
              KRB's Certification Pathway
              <span className="block mt-1 text-xl lg:text-2xl bg-gradient-to-r from-[#1976D2] to-[#1565C0] bg-clip-text text-transparent">
                Ensures End-to-End Compliance
              </span>
            </h3>
          </div>

          {/* Creative Pathway Visualization */}
          <div className="relative">
            {/* Connection Lines - Desktop */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#1976D2] to-[#1565C0] -translate-y-1/2 opacity-20" />

            {/* Pathway Steps */}
            <div className="grid lg:grid-cols-4 gap-4">
              {pathway.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="relative group"
                >
                  {/* Step Number Badge */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-[#1976D2] to-[#1565C0] rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-all" />
                      <div className="relative bg-gradient-to-br from-[#1976D2] to-[#1565C0] rounded-full w-10 h-10 flex items-center justify-center shadow-xl border-4 border-white">
                        <span className="text-base font-black text-white">
                          {index + 1}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card */}
                  <div className="relative mt-7">
                    <div className="absolute -inset-1 bg-gradient-to-r from-[#1976D2] to-[#1565C0] rounded-2xl opacity-0 group-hover:opacity-50 blur transition-all" />
                    <div className="relative bg-white border-2 border-gray-100 group-hover:border-transparent rounded-2xl p-5 text-center h-full shadow-lg transition-all">
                      {/* Icon */}
                      <div className="inline-flex mb-3">
                        <div className="bg-gradient-to-br from-[#1976D2] to-[#1565C0] rounded-xl p-3 shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all">
                          <step.icon className="w-6 h-6 text-white" />
                        </div>
                      </div>

                      {/* Content */}
                      <h4 className="text-base font-black text-gray-900 mb-2">
                        {step.title}
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>

                  {/* Arrow Connector - Desktop */}
                  {index < pathway.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-2 z-20">
                      <ArrowRight className="w-6 h-6 text-[#1976D2]" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="absolute -inset-1 bg-gradient-to-r from-[#1976D2] to-[#1565C0] rounded-3xl opacity-30 blur-2xl" />
          <div className="relative bg-gradient-to-r from-[#1976D2] to-[#1565C0] rounded-3xl p-10 lg:p-12 text-center overflow-hidden shadow-2xl">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24" />

            {/* Content */}
            <div className="relative">
              <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-md rounded-full px-6 py-3 mb-5">
                <Zap className="w-5 h-5 text-white" />
                <span className="text-white font-bold">
                  Ready for Global Markets
                </span>
              </div>

              <h3 className="text-2xl lg:text-3xl xl:text-4xl font-black text-white mb-4 leading-tight">
                Export-Ready Certified Feedstock
              </h3>

              <p className="text-base lg:text-lg text-white/95 max-w-3xl mx-auto mb-6 leading-relaxed">
                Every batch is ISCC-ready with complete traceability, quality
                documentation, and compliance verification - ensuring seamless
                global trade
              </p>

              <div className="flex flex-wrap justify-center gap-3">
                {[
                  "ISCC Certified",
                  "100% Traceable",
                  "Export Compliant",
                  "Quality Assured",
                ].map((badge, index) => (
                  <div
                    key={index}
                    className="bg-white/20 backdrop-blur-md border-2 border-white/40 rounded-full px-5 py-2.5"
                  >
                    <span className="text-white font-bold text-sm">
                      {badge}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}